<template>
  <div class="exam-management">
    <el-row :gutter="20">
      <!-- Room Management -->
      <el-col :span="6">
        <el-card>
          <template #header>
            <div class="card-header">
              <span>考场管理</span>
              <el-button type="primary" size="small" @click="showRoomDialog">
                <el-icon>
                  <Plus />
                </el-icon>
              </el-button>
            </div>
          </template>
          <el-table :data="rooms" v-loading="loadingRooms" size="small">
            <el-table-column prop="name" label="考场" width="80" />
            <el-table-column prop="alias" label="别名" width="100" />
            <el-table-column prop="building" label="楼栋" />
            <el-table-column prop="capacity" label="容量" width="60" />
            <el-table-column label="操作" width="100">
              <template #default="{ row }">
                <el-button text type="primary" size="small" @click="editRoom(row)">编辑</el-button>
                <el-popconfirm title="确定删除？" @confirm="deleteRoom(row.id)">
                  <template #reference>
                    <el-button text type="danger" size="small">删除</el-button>
                  </template>
                </el-popconfirm>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>

      <!-- Exam List -->
      <el-col :span="18">
        <el-card>
          <template #header>
            <div class="card-header">
              <div>
                <span>考试列表</span>
                <el-select v-model="selectedSemesterId" placeholder="选择学期" size="small"
                  style="margin-left: 16px; width: 200px" @change="fetchExams">
                  <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
                </el-select>
              </div>
              <el-button type="primary" @click="showExamDialog" :disabled="!selectedSemesterId">
                <el-icon>
                  <Plus />
                </el-icon>
                新建考试
              </el-button>
            </div>
          </template>

          <el-table :data="exams" v-loading="loadingExams" stripe>
            <el-table-column prop="name" label="考试名称" min-width="200" />
            <el-table-column label="场次数" width="100">
              <template #default="{ row }">
                {{ row.sessions?.length || 0 }} 场
              </template>
            </el-table-column>
            <el-table-column label="科目/年级" min-width="200">
              <template #default="{ row }">
                <el-tag v-for="s in row.sessions?.filter(sess => sess.subject_name)" :key="s.id" size="small"
                  :type="getGradeTagType(s.grade)" style="margin-right: 4px; margin-bottom: 2px">
                  {{ s.grade }} {{ s.subject_name }}
                </el-tag>
                <span v-if="!row.sessions?.some(s => s.subject_name)" style="color: #6b7280">未配置</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="220" fixed="right">
              <template #default="{ row }">
                <el-button text type="primary" @click="configureExam(row)">配置</el-button>
                <el-button text type="success" @click="goToSchedule(row.id)" :disabled="!row.sessions?.length">
                  排程
                </el-button>
                <el-popconfirm title="确定删除？" @confirm="deleteExam(row.id)">
                  <template #reference>
                    <el-button text type="danger">删除</el-button>
                  </template>
                </el-popconfirm>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <!-- Room Dialog -->
    <el-dialog v-model="roomDialogVisible" :title="roomForm.id ? '编辑考场' : '新建考场'" width="400px">
      <el-form :model="roomForm" label-width="80px">
        <el-form-item label="考场名称">
          <el-input v-model="roomForm.name" placeholder="如：101、A201" />
        </el-form-item>
        <el-form-item label="别名">
          <el-input v-model="roomForm.alias" placeholder="如：高三1考场" />
        </el-form-item>
        <el-form-item label="楼栋">
          <el-input v-model="roomForm.building" placeholder="如：教学楼A" />
        </el-form-item>
        <el-form-item label="容量">
          <el-input-number v-model="roomForm.capacity" :min="10" :max="200" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="roomDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitRoom" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>

    <!-- Exam Dialog -->
    <el-dialog v-model="examDialogVisible" title="新建考试" width="500px">
      <el-form :model="examForm" label-width="100px">
        <el-form-item label="考试名称">
          <el-input v-model="examForm.name" placeholder="如：2025-2026学年第一学期期中考试" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="examForm.description" type="textarea" rows="3" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="examDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitExam" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>

    <!-- Exam Configuration Dialog - 新版：场次直接包含科目和考场 -->
    <el-dialog v-model="configDialogVisible" :title="`配置考试：${currentExam?.name || ''}`" width="900px">
      <div class="config-section">
        <h4 style="margin-bottom: 16px">添加考试场次</h4>
        <el-form inline @submit.prevent="addSession">
          <el-form-item label="日期">
            <el-date-picker v-model="sessionForm.exam_date" type="date" value-format="YYYY-MM-DD"
              style="width: 130px" />
          </el-form-item>
          <el-form-item label="开始">
            <el-time-picker v-model="sessionForm.start_time" format="HH:mm" value-format="HH:mm:ss"
              style="width: 100px" />
          </el-form-item>
          <el-form-item label="结束">
            <el-time-picker v-model="sessionForm.end_time" format="HH:mm" value-format="HH:mm:ss"
              style="width: 100px" />
          </el-form-item>
          <el-form-item label="年级">
            <el-select v-model="sessionForm.grade" style="width: 80px">
              <el-option label="高一" value="高一" />
              <el-option label="高二" value="高二" />
              <el-option label="高三" value="高三" />
            </el-select>
          </el-form-item>
          <el-form-item label="科目">
            <el-select v-model="sessionForm.subject_id" style="width: 100px">
              <el-option v-for="s in subjects" :key="s.id" :label="s.name" :value="s.id" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="addSession">添加场次</el-button>
            <el-button type="success" @click="showBatchImportDialog">批量导入</el-button>
          </el-form-item>
        </el-form>

        <el-table :data="currentExam?.sessions || []" size="small" style="margin-top: 16px">
          <el-table-column prop="exam_date" label="日期" width="110" />
          <el-table-column label="时间" width="130">
            <template #default="{ row }">
              {{ formatTime(row.start_time) }} - {{ formatTime(row.end_time) }}
            </template>
          </el-table-column>
          <el-table-column label="年级" width="70">
            <template #default="{ row }">
              <el-tag :type="getGradeTagType(row.grade)" size="small">
                {{ row.grade || '未设置' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="科目" width="80">
            <template #default="{ row }">
              {{ row.subject_name || '未设置' }}
            </template>
          </el-table-column>
          <el-table-column label="考场">
            <template #default="{ row }">
              <el-tag v-for="r in row.exam_rooms" :key="r.id" size="small" type="info" style="margin-right: 4px"
                closable @close="removeRoomFromSession(row.id, r.room_id)">
                {{ r.room_name }} ({{ r.required_invigilators }}人)
              </el-tag>
              <el-button text type="primary" size="small" @click="showRoomAssignDialog(row)">
                配置考场
              </el-button>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="80">
            <template #default="{ row }">
              <el-popconfirm title="确定删除？" @confirm="deleteSession(row.id)">
                <template #reference>
                  <el-button text type="danger" size="small">删除</el-button>
                </template>
              </el-popconfirm>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>

    <!-- Room Assignment Dialog -->
    <el-dialog v-model="roomAssignDialogVisible" title="配置考场" width="500px">
      <el-form label-width="120px">
        <el-form-item label="选择考场">
          <div style="width: 100%; margin-bottom: 8px">
            <el-checkbox v-model="selectAllRooms" @change="handleSelectAllRooms">全选</el-checkbox>
          </div>
          <el-checkbox-group v-model="selectedRoomIds" @change="handleRoomSelectionChange">
            <el-checkbox v-for="r in rooms" :key="r.id" :label="r.id">
              {{ r.building }} {{ r.name }}
            </el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="每室监考人数">
          <el-input-number v-model="roomAssignForm.required_invigilators" :min="1" :max="5" />
        </el-form-item>
        <el-form-item label="批量应用">
          <el-checkbox v-model="applyToAllSessions">应用到该考试其他所有场次</el-checkbox>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="roomAssignDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitRoomAssignment" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>

    <!-- Batch Import Dialog -->
    <el-dialog v-model="batchImportDialogVisible" title="批量导入场次" width="700px">
      <div class="batch-import-container">
        <div class="import-hint">
          <p>请输入场次信息，每行一个场次。格式：<strong>日期 时间段 年级 科目</strong></p>
          <p>例如：<code>2026-01-20 08:00-10:00 高一 语文</code></p>
          <p>支持空格、制表符或逗号分隔。科目名称需与系统中一致。</p>
        </div>
        <el-input v-model="batchImportText" type="textarea" :rows="10" placeholder="粘贴场次信息到这里..."
          @input="parseBatchText" />

        <div v-if="parsedSessions.length > 0" class="preview-section">
          <h4>解析预览 ({{ parsedSessions.length }} 场)</h4>
          <el-table :data="parsedSessions" size="small" max-height="250">
            <el-table-column prop="exam_date" label="日期" width="100" />
            <el-table-column label="时间" width="120">
              <template #default="{ row }">
                {{ row.start_time }}-{{ row.end_time }}
              </template>
            </el-table-column>
            <el-table-column prop="grade" label="年级" width="70" />
            <el-table-column prop="subject_name" label="科目" />
            <el-table-column label="状态" width="80">
              <template #default="{ row }">
                <el-tag :type="row.subject_id ? 'success' : 'danger'" size="small">
                  {{ row.subject_id ? '就绪' : '科目未知' }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <template #footer>
        <el-button @click="batchImportDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitBatchImport" :loading="submitting"
          :disabled="parsedSessions.length === 0 || parsedSessions.some(s => !s.subject_id)">
          确认导入
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Plus } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { semesterApi, teacherApi, examApi } from '@/api'

const router = useRouter()

const semesters = ref([])
const subjects = ref([])
const rooms = ref([])
const exams = ref([])
const selectedSemesterId = ref(null)

const loadingRooms = ref(false)
const loadingExams = ref(false)
const submitting = ref(false)

const roomDialogVisible = ref(false)
const examDialogVisible = ref(false)
const configDialogVisible = ref(false)
const roomAssignDialogVisible = ref(false)
const batchImportDialogVisible = ref(false)
const currentExam = ref(null)
const currentSession = ref(null)

const batchImportText = ref('')
const parsedSessions = ref([])

const roomForm = ref({ id: null, name: '', alias: '', building: '', capacity: 30 })
const examForm = ref({ name: '', description: '' })
const sessionForm = ref({
  exam_date: '',
  start_time: '',
  end_time: '',
  session_name: '',
  subject_id: null,
  grade: '高一'
})
const selectedRoomIds = ref([])
const roomAssignForm = ref({ required_invigilators: 1 })
const selectAllRooms = ref(false)
const applyToAllSessions = ref(false)

const handleSelectAllRooms = (val) => {
  selectedRoomIds.value = val ? rooms.value.map(r => r.id) : []
}

const handleRoomSelectionChange = (value) => {
  selectAllRooms.value = value.length === rooms.value.length && rooms.value.length > 0
}

const fetchSemesters = async () => {
  try {
    semesters.value = await semesterApi.getAll()
    if (semesters.value.length > 0) {
      const active = semesters.value.find(s => s.is_active)
      selectedSemesterId.value = active?.id || semesters.value[0].id
      fetchExams()
    }
  } catch (e) {
    console.error(e)
  }
}

const fetchSubjects = async () => {
  try {
    subjects.value = await teacherApi.getSubjects()
  } catch (e) {
    console.error(e)
  }
}

const fetchRooms = async () => {
  loadingRooms.value = true
  try {
    rooms.value = await examApi.getRooms()
  } catch (e) {
    console.error(e)
  } finally {
    loadingRooms.value = false
  }
}

const fetchExams = async () => {
  if (!selectedSemesterId.value) return
  loadingExams.value = true
  try {
    exams.value = await examApi.getAll(selectedSemesterId.value)
  } catch (e) {
    console.error(e)
  } finally {
    loadingExams.value = false
  }
}

const showRoomDialog = () => {
  roomForm.value = { id: null, name: '', alias: '', building: '', capacity: 30 }
  roomDialogVisible.value = true
}

const editRoom = (row) => {
  roomForm.value = { ...row }
  roomDialogVisible.value = true
}

const submitRoom = async () => {
  if (!roomForm.value.name || !roomForm.value.building) {
    ElMessage.warning('请填写完整信息')
    return
  }
  submitting.value = true
  try {
    if (roomForm.value.id) {
      await examApi.updateRoom(roomForm.value.id, roomForm.value)
      ElMessage.success('考场更新成功')
    } else {
      await examApi.createRoom(roomForm.value)
      ElMessage.success('考场创建成功')
    }
    roomDialogVisible.value = false
    fetchRooms()
  } catch (e) {
    console.error(e)
  } finally {
    submitting.value = false
  }
}

const deleteRoom = async (id) => {
  try {
    await examApi.deleteRoom(id)
    ElMessage.success('考场已删除')
    fetchRooms()
  } catch (e) {
    console.error(e)
  }
}

const showExamDialog = () => {
  examForm.value = { name: '', description: '' }
  examDialogVisible.value = true
}

const submitExam = async () => {
  if (!examForm.value.name) {
    ElMessage.warning('请输入考试名称')
    return
  }
  submitting.value = true
  try {
    await examApi.create({
      ...examForm.value,
      semester_id: selectedSemesterId.value
    })
    ElMessage.success('考试创建成功')
    examDialogVisible.value = false
    fetchExams()
  } catch (e) {
    console.error(e)
  } finally {
    submitting.value = false
  }
}

const deleteExam = async (id) => {
  try {
    await examApi.delete(id)
    ElMessage.success('考试已删除')
    fetchExams()
  } catch (e) {
    console.error(e)
  }
}

const configureExam = async (exam) => {
  currentExam.value = exam
  configDialogVisible.value = true
}

const reloadCurrentExam = async () => {
  if (!currentExam.value?.id) return
  try {
    currentExam.value = await examApi.getById(currentExam.value.id)
  } catch (e) {
    console.error(e)
  }
}

const addSession = async () => {
  if (!sessionForm.value.exam_date || !sessionForm.value.start_time || !sessionForm.value.end_time) {
    ElMessage.warning('请填写日期和时间')
    return
  }
  try {
    await examApi.addSession(currentExam.value.id, sessionForm.value)
    ElMessage.success('场次添加成功')
    sessionForm.value = {
      exam_date: sessionForm.value.exam_date, // 保留日期方便连续添加
      start_time: '',
      end_time: '',
      session_name: '',
      subject_id: null,
      grade: sessionForm.value.grade // 保留年级
    }
    await reloadCurrentExam()
    fetchExams()
  } catch (e) {
    console.error(e)
  }
}

const deleteSession = async (id) => {
  try {
    await examApi.deleteSession(id)
    ElMessage.success('场次已删除')
    await reloadCurrentExam()
    fetchExams()
  } catch (e) {
    console.error(e)
  }
}

const showRoomAssignDialog = (session) => {
  currentSession.value = session
  selectedRoomIds.value = session.exam_rooms?.map(r => r.room_id) || []
  selectAllRooms.value = selectedRoomIds.value.length > 0 && selectedRoomIds.value.length === rooms.value.length
  applyToAllSessions.value = false
  roomAssignForm.value = { required_invigilators: 1 }
  roomAssignDialogVisible.value = true
}

const submitRoomAssignment = async () => {
  submitting.value = true
  try {
    if (applyToAllSessions.value && currentExam.value?.sessions?.length) {
      const promises = currentExam.value.sessions.map(s =>
        examApi.assignRoomsToSession(
          s.id,
          selectedRoomIds.value,
          roomAssignForm.value.required_invigilators
        )
      )
      await Promise.all(promises)
    } else {
      await examApi.assignRoomsToSession(
        currentSession.value.id,
        selectedRoomIds.value,
        roomAssignForm.value.required_invigilators
      )
    }
    ElMessage.success('考场配置成功')
    roomAssignDialogVisible.value = false
    await reloadCurrentExam()
    fetchExams()
  } catch (e) {
    console.error(e)
  } finally {
    submitting.value = false
  }
}

const removeRoomFromSession = async (sessionId, roomId) => {
  try {
    await examApi.removeRoomFromSession(sessionId, roomId)
    ElMessage.success('考场已移除')
    await reloadCurrentExam()
    fetchExams()
  } catch (e) {
    console.error(e)
  }
}

const showBatchImportDialog = () => {
  batchImportText.value = ''
  parsedSessions.value = []
  batchImportDialogVisible.value = true
}

const parseBatchText = () => {
  if (!batchImportText.value.trim()) {
    parsedSessions.value = []
    return
  }

  const lines = batchImportText.value.split('\n')
  const results = []

  lines.forEach(line => {
    if (!line.trim()) return

    // Split by whitespace, tab, or comma
    const parts = line.trim().split(/[\s,\t]+/)
    if (parts.length < 4) return

    const dateStr = parts[0]
    const timeRange = parts[1]
    const grade = parts[2]
    const subjectName = parts[3]

    // Parse time range (e.g., 08:00-10:00 or 08:00~10:00)
    const timeParts = timeRange.split(/[-~]/)
    if (timeParts.length !== 2) return

    let startTime = timeParts[0].trim()
    let endTime = timeParts[1].trim()

    // Ensure HH:mm:ss format
    if (startTime.length === 5) startTime += ':00'
    if (endTime.length === 5) endTime += ':00'

    // Match subject
    const subject = subjects.value.find(s => s.name === subjectName)

    results.push({
      exam_date: dateStr,
      start_time: startTime,
      end_time: endTime,
      grade: grade,
      subject_id: subject?.id || null,
      subject_name: subjectName,
      rooms: []
    })
  })

  parsedSessions.value = results
}

const submitBatchImport = async () => {
  if (parsedSessions.value.length === 0) return

  submitting.value = true
  try {
    await examApi.batchAddSessions(currentExam.value.id, {
      sessions: parsedSessions.value
    })
    ElMessage.success(`成功导入 ${parsedSessions.value.length} 个场次`)
    batchImportDialogVisible.value = false
    await reloadCurrentExam()
    fetchExams()
  } catch (e) {
    console.error(e)
    ElMessage.error('导入失败，请检查格式是否正确')
  } finally {
    submitting.value = false
  }
}

const formatTime = (time) => {
  if (!time) return ''
  return time.substring(0, 5) // HH:mm:ss -> HH:mm
}

const getGradeTagType = (grade) => {
  const types = { '高一': 'success', '高二': 'warning', '高三': 'danger' }
  return types[grade] || 'info'
}

const goToSchedule = (examId) => {
  router.push({ name: 'Schedules', params: { examId } })
}

onMounted(() => {
  fetchSemesters()
  fetchSubjects()
  fetchRooms()
})
</script>

<style scoped>
.exam-management {
  max-width: 1600px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header span {
  font-size: 16px;
  font-weight: 500;
}

.config-section {
  padding: 10px 0;
}

h4 {
  color: #303133;
  font-weight: 500;
}

.import-hint {
  background-color: #f0f9eb;
  border-left: 5px solid #67c23a;
  padding: 10px 15px;
  margin-bottom: 15px;
  font-size: 13px;
  color: #606266;
}

.import-hint p {
  margin: 4px 0;
}

.preview-section {
  margin-top: 20px;
}

.preview-section h4 {
  margin-bottom: 10px;
}
</style>
